import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api, { formatError, photoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Heart, Check, X, Clock, MessageSquare, Phone, Mail, MessageCircle, Lock, Crown } from "lucide-react";
import { toast } from "sonner";

export default function Interests() {
  const location = useLocation();
  const defaultTab = new URLSearchParams(location.search).get("tab") || "received";
  
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [mutual, setMutual] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, s, m] = await Promise.all([
        api.get("/interests", { params: { direction: "received" } }),
        api.get("/interests", { params: { direction: "sent" } }),
        api.get("/interests", { params: { direction: "mutual" } }),
      ]);
      setReceived(r.data); 
      setSent(s.data);
      setMutual(m.data);
    } catch (e) { 
      toast.error(formatError(e)); 
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    loadAll(); 
  }, []);

  const respond = async (interestId, action) => {
    try {
      await api.post(`/interests/${interestId}/respond?action=${action}`);
      toast.success(action === "accept" ? "Interest accepted! Contact unlocked." : "Declined");
      loadAll();
    } catch (e) { 
      toast.error(formatError(e)); 
    }
  };

  const withdraw = async (interestId) => {
    try {
      await api.delete(`/interests/${interestId}`);
      toast.success("Interest withdrawn successfully");
      loadAll();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <div className="container-px py-8 max-w-5xl mx-auto">
      <span className="label-cap">Matches & Interests</span>
      <h1 className="font-heading text-4xl text-primary mt-1">Interests Manager</h1>
      <p className="text-muted-foreground text-sm mt-1">Accept interests to unlock phone numbers, emails, and start chatting.</p>

      {loading ? (
        <div className="text-center py-20 text-muted-foreground">Loading connections...</div>
      ) : (
        <Tabs defaultValue={defaultTab} className="mt-6">
          <TabsList className="rounded-full bg-muted/60 p-1">
            <TabsTrigger value="received" className="rounded-full" data-testid="tab-received">
              Received ({received.length})
            </TabsTrigger>
            <TabsTrigger value="sent" className="rounded-full" data-testid="tab-sent">
              Sent ({sent.length})
            </TabsTrigger>
            <TabsTrigger value="mutual" className="rounded-full" data-testid="tab-mutual">
              Mutual ({mutual.length})
            </TabsTrigger>
          </TabsList>

          {/* Received Interests Content */}
          <TabsContent value="received" className="mt-6">
            <List items={received} render={(it) => (
              <Row key={it.interest_id} it={it}>
                {it.status === "pending" ? (
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => respond(it.interest_id, "accept")} 
                      className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground" 
                      data-testid={`accept-${it.interest_id}`}
                    >
                      <Check className="w-4 h-4 mr-1"/>Accept
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => respond(it.interest_id, "decline")} 
                      className="rounded-full" 
                      data-testid={`decline-${it.interest_id}`}
                    >
                      <X className="w-4 h-4 mr-1"/>Decline
                    </Button>
                  </div>
                ) : (
                  <StatusPill s={it.status} />
                )}
              </Row>
            )} />
          </TabsContent>

          {/* Sent Interests Content */}
          <TabsContent value="sent" className="mt-6">
            <List items={sent} render={(it) => (
              <Row key={it.interest_id} it={it}>
                <div className="flex items-center gap-2">
                  <StatusPill s={it.status} />
                  {it.status === "pending" && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => withdraw(it.interest_id)} 
                      className="rounded-full text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                      data-testid={`withdraw-${it.interest_id}`}
                    >
                      Withdraw
                    </Button>
                  )}
                </div>
              </Row>
            )} />
          </TabsContent>

          {/* Mutual Matches Content */}
          <TabsContent value="mutual" className="mt-6">
            <List 
              items={mutual} 
              render={(it) => (
                <div key={it.interest_id} className="card-soft p-5 flex flex-col justify-between border-emerald-500/20 bg-emerald-500/5">
                  {/* Top user profile block */}
                  <div className="flex items-start gap-4">
                    <Link to={`/app/profile/${it.user.user_id}`} className="shrink-0">
                      <img 
                        src={photoUrl(it.user.photos?.[0]) || "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&auto=format&fit=crop&q=40"} 
                        alt="" 
                        className="w-16 h-16 rounded-2xl object-cover border border-emerald-500/20 shadow-sm" 
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <Link to={`/app/profile/${it.user.user_id}`} className="font-heading text-lg text-primary hover:underline truncate font-semibold block">
                          {it.user.name}
                        </Link>
                        <span className="text-[10px] bg-emerald-500/10 text-emerald-700 px-2 py-0.5 rounded-full font-bold">Mutual</span>
                      </div>
                      <div className="text-xs text-muted-foreground truncate mt-0.5">
                        {it.user.age ? `${it.user.age} yrs · ` : ""}{it.user.district} · {it.user.occupation}
                      </div>
                    </div>
                  </div>

                  {/* Unlocked Contact Details Block */}
                  {it.user.contact_locked ? (
                    <div className="mt-4 p-4 rounded-2xl bg-secondary/5 border border-dashed border-secondary/30 text-center">
                      <Lock className="w-6 h-6 text-secondary mx-auto mb-2" />
                      <p className="font-semibold text-primary text-xs">Contact Details Locked</p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Free users can only view 3 mutual contacts. Upgrade to Premium to see unlimited contacts.
                      </p>
                      <Link 
                        to="/app/premium" 
                        className="mt-2.5 inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground text-[10px] font-bold gap-1 transition-colors"
                      >
                        <Crown className="w-3 h-3" /> Upgrade now
                      </Link>
                    </div>
                  ) : (
                     <div className="mt-4 p-3.5 rounded-2xl bg-background border border-emerald-500/10 space-y-2 text-xs">
                      <div className="flex items-center gap-2 text-primary">
                        <Phone className="w-3.5 h-3.5 text-secondary" />
                        <span className="font-semibold">Phone:</span>
                        <a href={`tel:${it.user.phone}`} className="hover:underline select-all">{it.user.phone || "Not provided"}</a>
                      </div>
                      <div className="flex items-center gap-2 text-primary">
                        <Mail className="w-3.5 h-3.5 text-secondary" />
                        <span className="font-semibold">Email:</span>
                        <a href={`mailto:${it.user.email}`} className="hover:underline select-all">{it.user.email || "Not provided"}</a>
                      </div>
                    </div>
                  )}

                  {/* Actions buttons */}
                  <div className="mt-4 pt-3 border-t border-border/40 flex flex-wrap gap-2 justify-between items-center">
                    <div className="flex gap-2">
                      {it.user.phone && (
                        <a 
                          href={`https://wa.me/${it.user.phone.replace("+", "")}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full text-xs font-semibold shadow-sm flex items-center justify-center shrink-0"
                          title="WhatsApp Chat"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                    <Button asChild size="sm" className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid={`chat-${it.user.user_id}`}>
                      <Link to={`/app/chat/${it.user.user_id}`}>
                        <MessageSquare className="w-4 h-4 mr-1.5" /> Start Private Chat
                      </Link>
                    </Button>
                  </div>

                </div>
              )} 
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function List({ items, render }) {
  if (!items.length) {
    return (
      <div className="text-center py-16 border border-dashed border-border rounded-2xl text-muted-foreground italic">
        No connection requests in this category.
      </div>
    );
  }
  return <div className="grid sm:grid-cols-2 gap-5">{items.map(render)}</div>;
}

function Row({ it, children }) {
  return (
    <div className="card-soft p-4 flex items-center gap-4">
      <Link to={`/app/profile/${it.user.user_id}`} className="shrink-0">
        <img 
          src={photoUrl(it.user.photos?.[0]) || "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&auto=format&fit=crop&q=40"} 
          alt="" 
          className="w-16 h-16 rounded-2xl object-cover border border-border" 
        />
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/app/profile/${it.user.user_id}`} className="font-heading text-lg text-primary hover:underline truncate block font-semibold">
          {it.user.name}
        </Link>
        <div className="text-xs text-muted-foreground truncate mt-0.5">
          {it.user.age ? `${it.user.age} yrs · ` : ""}{it.user.district} · {it.user.occupation}
        </div>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    pending: { icon: Clock, label: "Pending", cls: "bg-secondary/15 text-secondary border border-secondary/10" },
    accepted: { icon: Check, label: "Accepted", cls: "bg-emerald-100 text-emerald-700 border border-emerald-200" },
    declined: { icon: X, label: "Declined", cls: "bg-muted text-muted-foreground border border-border" },
  }[s] || { icon: Heart, label: s, cls: "bg-muted border border-border" };
  
  const Icon = map.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-semibold ${map.cls}`}>
      <Icon className="w-3.5 h-3.5"/>
      {map.label}
    </span>
  );
}
